import JSZip from 'jszip'
import * as XLSX from 'xlsx'
import sizeOf from 'image-size'
import { saveAs } from 'file-saver'
import type { RcFile } from 'antd/lib/upload'
import { showNotification } from './notifications'
import { UploadFile } from 'antd/lib/upload/interface'

export const getFileFormUrl = async (url: string | File, name?: string) => {
  if (url instanceof File) {
    return url
  }
  const filename = name || url.substring(url.lastIndexOf('/') + 1)
  url += '?' + new Date().getTime()
  const response = await fetch(url).then(res => res.blob())
  return new File([response], filename)
}

export const getFileFromBlobUrl = async (url: string | File, name?: string) => {
  if (url instanceof File) {
    return url
  }
  const filename = name || url.substring(url.lastIndexOf('/') + 1)
  if (url?.includes('aws') && !url?.includes('?')) {
    url += '?' + new Date().getTime()
  }
  const response = await fetch(url).then(res => res.blob())
  const extension = response?.type?.split('/')[1].includes('octet-stream') ?
    filename.split('.').pop() : response?.type?.split('/')[1]
  const finalExtension = filename?.endsWith(`.${extension}`) ? '' : `.${extension}`
  return new File([response], `${filename}${finalExtension}`,{ type: response.type })
}

export const extractURL = (file: File | string) => {
  if (process) {
    if (!(file instanceof File)) {
      return Promise.resolve(file)
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = error => reject(error)
    })
  }
}

export const readExcelFile = (file: any, handleExcelData: any) => {
  const reader = new FileReader()
  reader.onload = (evt) => {
    const bstr = evt.target.result
    const wb = XLSX.read(bstr, { type: 'binary' })
    const wsname = wb.SheetNames[0]
    const ws = wb.Sheets[wsname]
    const data = XLSX.utils.sheet_to_json(ws)
    const { keys, finalData } = prepareExcel(data)
    handleExcelData(keys, finalData)
  }
  reader.readAsBinaryString(file)
}

export const prepareExcel = (data: any) => {
  const finalData = []
  for (const i of data) {
    for (const x of Object.keys(i)) {
      const newKey = x.replace(/ /g, '')
      if (x.includes(' ')) {
        delete Object.assign(i, { [newKey]: i[x] })[x]
      }
    }
    finalData.push(i)
  }
  const values = { keys: null, finalData: null }
  if (finalData.length > 0) {
    const keys = Object.keys(finalData[0])
    values['keys'] = keys
  }
  values['finalData'] = finalData
  return values
}


export const getImageBase64 = (file: RcFile): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = error => reject(error)
  })
}


export const exportPdfZip = async (fileName: string, certificates: { name: string, url: string }[]) => {
  const zip = new JSZip()
  const promises = certificates.map(async certificate => {
    const response = await fetch(certificate.url)
    const blob = await response.blob()
    zip.file(`${certificate.name}.pdf`, blob, { binary: true })
  })
  await Promise.all(promises)
  zip.generateAsync({ type: 'blob' }).then(content => {
    saveAs(content, fileName)
  })
}

export const exportXLSXZip = async (
  fileName: string,
  certificates: { name: string, url: string }[],
  extension='pdf',
  extraData=null
) => {
  const zip = new JSZip()
  const promises = certificates.map(async certificate => {
    try {
      const response = await fetch(certificate.url)
      const blob = await response.blob()
      zip.file(`${certificate.name}.${extension}`, blob, { binary: true })
    } catch (error) {
      if (certificate.url.split('https://').length > 2) {
        const newUrl = `https://${certificate.url.split('https://').pop()}`
        const response = await fetch(newUrl)
        const blob = await response.blob()
        zip.file(`${certificate.name}.${extension}`, blob, { binary: true })
      } else {
        showNotification('error',`Error fetching file: ${certificate.name}`)
      }
    }
  })
  if (extraData) {
    zip.file(`${extraData.fileName}.xlsx`, extraData.binary, { binary: true })
  }
  await Promise.all(promises)
  zip.generateAsync({ type: 'blob' }).then(content => {
    saveAs(content, fileName)
  })
}

export const readFileAsBuffer = (file: File): Promise<Buffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const buffer = Buffer.from(reader.result as ArrayBuffer)
      resolve(buffer)
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> =>{
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = function() {
      const img = new Image()
      img.onload = function() {
        resolve({ width: img.width, height: img.height })
      }
      img.onerror = function() {
        reject(new Error('Failed to load image'))
      }
      img.src = reader.result as string
    }
    reader.onerror = function() {
      reject(new Error('Failed to read file'))
    }
    reader.readAsDataURL(file)
  })
}

export const getImageDimensionsSizeOf = async (file: File) => {
  const buffer = await readFileAsBuffer(file)
  return sizeOf(buffer)
}
export const getFileSizeInMB = (file: File | UploadFile) => {
  return file.size / 1024 / 1024 // convert byte to MB
}

export const convertToExcel = (data: any, fileName: string) => {
  const book = XLSX.utils.book_new()
  const sheet = XLSX.utils.json_to_sheet([data])
  XLSX.utils.book_append_sheet(book, sheet, 'Sheet1')
  const wbout = XLSX.write(book, { bookType: 'xlsx', type: 'binary' })
  const buf = new ArrayBuffer(wbout.length)
  const view = new Uint8Array(buf)
  for (let i=0; i<wbout.length; i++) view[i] = wbout.charCodeAt(i) & 0xFF
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  return { fileName: `${fileName}.xlsx`, binary: blob }
}
